/**
 * Browser video-message capture.
 *
 * Mirrors VoiceRecorder but requests both audio + video tracks and
 * exposes the live MediaStream so the UI can show a webcam preview
 * during capture. The recording is muxed into a single container
 * (webm or mp4 depending on browser support) and handed back as a
 * Blob ready for the AttachmentService pipeline.
 */

const PREFERRED_MIMES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
] as const;

export interface VideoRecording {
  readonly bytes: Uint8Array;
  readonly mime: string;
  /** Duration in milliseconds. */
  readonly durationMs: number;
}

function pickMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const candidate of PREFERRED_MIMES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return undefined;
}

export class VideoRecorder {
  #recorder: MediaRecorder | undefined;
  #stream: MediaStream | undefined;
  #chunks: BlobPart[] = [];
  #startedAt = 0;
  #stopPromise: Promise<VideoRecording> | undefined;

  /**
   * Request camera + mic access and start recording. Throws if the
   * browser doesn't support MediaRecorder, the user denies the
   * permission, or no compatible MIME type is available.
   */
  async start(): Promise<void> {
    if (this.#recorder) throw new Error('already recording');
    const mime = pickMime();
    if (!mime) throw new Error('video recording not supported on this browser');
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('camera access not available');
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user' },
      audio: true,
    });
    const recorder = new MediaRecorder(stream, { mimeType: mime });
    this.#stream = stream;
    this.#recorder = recorder;
    this.#chunks = [];
    this.#startedAt = Date.now();
    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) this.#chunks.push(event.data);
    });
    this.#stopPromise = new Promise<VideoRecording>((resolve, reject) => {
      recorder.addEventListener('stop', () => {
        this.#stream?.getTracks().forEach((t) => {
          t.stop();
        });
        const blob = new Blob(this.#chunks, { type: mime });
        const durationMs = Date.now() - this.#startedAt;
        blob
          .arrayBuffer()
          .then((buf) => {
            resolve({ bytes: new Uint8Array(buf), mime, durationMs });
          })
          .catch(reject);
        this.#recorder = undefined;
        this.#stream = undefined;
      });
      recorder.addEventListener('error', () => {
        reject(new Error('recorder errored'));
      });
    });
    recorder.start();
  }

  /**
   * The live MediaStream — bind to a `<video srcObject>` to render
   * a preview while recording. Returns undefined if not currently
   * capturing.
   */
  getStream(): MediaStream | undefined {
    return this.#stream;
  }

  async stop(): Promise<VideoRecording> {
    if (!this.#recorder || !this.#stopPromise) throw new Error('not recording');
    if (this.#recorder.state !== 'inactive') this.#recorder.stop();
    return this.#stopPromise;
  }

  cancel(): void {
    try {
      if (this.#recorder && this.#recorder.state !== 'inactive') this.#recorder.stop();
    } catch {
      // already stopping
    }
    this.#stream?.getTracks().forEach((t) => {
      t.stop();
    });
    this.#recorder = undefined;
    this.#stream = undefined;
    this.#chunks = [];
    this.#stopPromise = undefined;
  }

  isRecording(): boolean {
    return this.#recorder !== undefined;
  }
}

export function isVideoRecordingAvailable(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    pickMime() !== undefined
  );
}
